import { z } from "zod";

export const adImageRecordSchema = z.object({
  provider: z.enum(["r2", "fal", "legacy"]),
  objectKey: z.string().optional(),
  contentType: z.string().optional(),
  sizeBytes: z.number().optional(),
  imageUrlOriginal: z.string().optional(),
  imageUrlDurable: z.string().optional(),
});

export type AdImageRecord = z.infer<typeof adImageRecordSchema>;

export const exampleImageEntrySchema = z.union([z.string(), adImageRecordSchema]);
export type ExampleImageEntry = z.infer<typeof exampleImageEntrySchema>;

export function normalizeExampleImage(entry: unknown): AdImageRecord | null {
  if (entry == null) return null;
  if (typeof entry === "string") {
    if (!entry.trim()) return null;
    return { provider: "legacy", imageUrlOriginal: entry };
  }
  if (typeof entry === "object") {
    const obj = entry as Record<string, unknown>;
    const provider = obj.provider;
    if (provider === "r2" || provider === "fal" || provider === "legacy") {
      return {
        provider,
        objectKey: typeof obj.objectKey === "string" ? obj.objectKey : undefined,
        contentType: typeof obj.contentType === "string" ? obj.contentType : undefined,
        sizeBytes: typeof obj.sizeBytes === "number" ? obj.sizeBytes : undefined,
        imageUrlOriginal: typeof obj.imageUrlOriginal === "string" ? obj.imageUrlOriginal : undefined,
        imageUrlDurable: typeof obj.imageUrlDurable === "string" ? obj.imageUrlDurable : undefined,
      };
    }
    if (typeof obj.imageUrlOriginal === "string" && obj.imageUrlOriginal.trim()) {
      return {
        provider: "legacy",
        imageUrlOriginal: obj.imageUrlOriginal,
        imageUrlDurable: typeof obj.imageUrlDurable === "string" ? obj.imageUrlDurable : undefined,
      };
    }
  }
  return null;
}

export function displayUrl(entry: AdImageRecord | null | undefined): string | null {
  if (!entry) return null;
  return entry.imageUrlDurable || entry.imageUrlOriginal || null;
}

// A loose shape covering every ad-unit field that participates in
// example-image eligibility. Both the client picker (AdUnitType) and the
// server (its `any` ad-unit row) satisfy this.
export interface ExampleImageAdUnit {
  isMeme?: boolean | null;
  memeReferenceImageUrl?: string | null;
  adgenExampleImageUrl?: string | null;
  exampleImageUrl?: string | null;
  exampleImages?: unknown;
}

// Picker eligibility: whether an ad unit has a usable example/reference
// image so it can actually be rendered. MUST stay in lockstep with the
// server's `resolveAdUnitExampleSource` in
// server/services/ad-image-generator.ts — a unit is eligible here iff the
// server resolves a non-empty example URL for it. If these diverge, a
// template the user can pick will hard-fail server-side with "no example
// image". Resolution order: meme units use their meme reference; any
// other unit uses adgenExampleImageUrl, then the legacy exampleImageUrl,
// then the first usable entry of the normalized `exampleImages` array.
// Guarded by server/__tests__/exampleImageParity.test.ts.
export function hasUsableExampleImage(unit: ExampleImageAdUnit): boolean {
  if (unit?.isMeme === true) {
    return !!(unit.memeReferenceImageUrl && unit.memeReferenceImageUrl.trim());
  }
  const adgen = unit?.adgenExampleImageUrl;
  if (typeof adgen === "string" && adgen.trim()) return true;
  const legacy = unit?.exampleImageUrl;
  if (typeof legacy === "string" && legacy.trim()) return true;
  const raw = unit?.exampleImages;
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const url = displayUrl(normalizeExampleImage(entry));
      if (url && url.trim()) return true;
    }
  }
  return false;
}

// Wraps a remote (https) image URL through the server-side resizing
// proxy so listing pages can request a small WebP thumbnail instead of
// the full-size original. Falls through unchanged for null/relative/data
// URIs and for already-thumbnailed urls.
export function thumbUrl(src: string | null | undefined, width = 400, quality = 75): string | null {
  if (!src) return null;
  if (!src.startsWith("https://")) return src;
  return `/api/img-proxy?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;
}
